/**
 * The host desk contract — §13 / ADR-0018 (waitlist + booking), pinned in one
 * file.
 *
 * Read directly off `/tmp/integrate` (`integrate/wave-a`, backend green) —
 * `src/modules/booking/{waitlist.controller.ts,host-queue.service.ts,dto/waitlist.dto.ts}`.
 *
 * THE POINT OF THE EPIC: the host queue holds reservations and walk-ins
 * TOGETHER, in one ordered list, because a host deciding who sits next is
 * doing one job regardless of which table came from which system
 * (`HostQueueService.queue`'s own docstring calls this "the definition of
 * done, as a read"). This file therefore pins both the queue projection and
 * the plain walk-in CRUD it is built from — never a stored, mergeable thing
 * of its own.
 *
 * Deliberately NOT here: the kids waiting list. `docs/fiscal.md`'s sibling
 * epics keep that concept separate on purpose — a named child, a named
 * guardian, a billed clock — and it already has its own screen (`/kids`,
 * `children.view`). Folding it into this queue would be exactly the mistake
 * ADR-0018 warns against.
 */
import { ReservationStatus } from '@/lib/shared-types';

export { ReservationStatus };

export const WaitlistStatus = {
  WAITING: 'WAITING',
  CALLED: 'CALLED',
  SEATED: 'SEATED',
  CANCELLED: 'CANCELLED',
  NO_SHOW: 'NO_SHOW',
} as const;
export type WaitlistStatus = (typeof WaitlistStatus)[keyof typeof WaitlistStatus];

export type HostQueueKind = 'RESERVATION' | 'WALK_IN';

export const HOST_QUEUE_ROUTES = {
  queue: '/waitlist/queue',
  waitlist: '/waitlist',
  waitlistEntry: (id: string) => `/waitlist/${id}`,
  call: (id: string) => `/waitlist/${id}/call`,
  seat: (id: string) => `/waitlist/${id}/seat`,
  noShow: (id: string) => `/waitlist/${id}/no-show`,
  cancel: (id: string) => `/waitlist/${id}/cancel`,
  reservationSeat: (id: string) => `/reservations/${id}/seat`,
  tables: '/tables',
} as const;

/** Mirrors `permission-catalogue.json`. Note `queue` itself needs BOTH `waitlist.view` AND `reservations.view` — `RequirePermissions` is AND, not OR. */
export const HOST_QUEUE_PERMISSIONS = {
  waitlistView: 'waitlist.view',
  reservationsView: 'reservations.view',
  join: 'waitlist.join',
  call: 'waitlist.call',
  seat: 'waitlist.seat',
  cancel: 'waitlist.cancel',
  reservationsUpdate: 'reservations.update',
} as const;

/** `GET /waitlist/queue` — one row, whichever system it came from. */
export interface HostQueueRow {
  kind: HostQueueKind;
  id: string;
  status: ReservationStatus | WaitlistStatus;
  guestName: string | null;
  guestPhone: string | null;
  partySize: number;
  /** The instant this row is due: the booked time, or when a walk-in was quoted for. */
  dueAt: string;
  /** Where they are in the walk-in line, or null for a booking. */
  position: number | null;
  tableId: string | null;
  notes: string | null;
  sessionId: string | null;
}

export interface HostQueueResponse {
  branch: { id: string; name: string; timezone: string };
  generatedAt: string;
  horizonMinutes: number;
  counts: { reservations: number; walkIns: number; covers: number };
  rows: HostQueueRow[];
}

export interface JoinWaitlistBody {
  branchId: string;
  partySize: number;
  guestName?: string;
  guestPhone?: string;
  notes?: string;
  operationId?: string;
}

export interface SeatWaitlistBody {
  tableId: string;
  guestCount?: number;
  waiterId?: string;
  operationId?: string;
}

export interface TableOption {
  id: string;
  number: string;
  name: string | null;
  seats: number;
  state: string;
  area?: { id: string; name: string } | null;
}

/**
 * The specific, coded 409s this screen must not present as a generic error
 * (the task's own "someone just seated this table" ask). `session_id` is
 * UNIQUE on both `Reservation` and `WaitlistEntry`, so seating a second party
 * onto a table that already has an open visit is refused by the database, not
 * by a validation message.
 */
export const DOUBLE_SEAT_CODES = new Set([
  'WAITLIST_TABLE_ALREADY_SEATED',
  'TABLE_ALREADY_SEATED',
  'BOOKING_VISIT_ALREADY_CLAIMED',
]);

export function apiMessage(error: unknown, fallback: string): string {
  const payload = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;
  if (Array.isArray(payload)) return payload.filter(Boolean).join(' · ');
  if (typeof payload === 'string' && payload.trim()) return payload;
  return fallback;
}

export function errorCode(error: unknown): string | null {
  const code = (error as { response?: { data?: { code?: unknown } } })?.response?.data?.code;
  return typeof code === 'string' ? code : null;
}

/** True when this failure is the "someone just seated this table" race, not an ordinary refusal. */
export function isDoubleSeatConflict(error: unknown): boolean {
  const code = errorCode(error);
  return code !== null && DOUBLE_SEAT_CODES.has(code);
}

export function newOperationId(prefix: string): string {
  const random =
    typeof crypto !== 'undefined' && 'randomUUID' in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2);
  return `dashboard:${prefix}:${random}`;
}
